import { useDispatch, useSelector } from 'react-redux';
import { setFilter, toggleFavouriteAction } from 'actions/search';
import {
  categories,
  categoriesAcademic,
  categoriesIndustry,
  categoriesLegacy,
  categoriesLegacyIndustry,
} from 'services/inputTokens';
import { Token } from 'components/common/TokenInput';

import { getFilter, getSearchRequest, getFavourites } from 'selectors/search';

import { Tooltip } from 'antd';

import { useAuth } from 'hooks';
import licenseType from 'models/licenseType';
import { AiIcon, StarredSection } from 'components/common';
import {
  useState,
  useCallback,
  useEffect,
  Fragment,
  useRef,
  useMemo,
} from 'react';
import { OrgCategoryContainer, OrgCategorySection } from './styled';
import { getStringValue, isExcluded, toExcluded } from 'utils/search';

import { Filter } from '../../helpers';

let isOpen = false;

const OrgCategory = ({ activeOnly }) => {
  const organisationCategory = 'organisationCategory';
  const valueFromStore = useSelector(getFilter(organisationCategory));
  const { loading } = useSelector(getSearchRequest);
  const favourites = useSelector(getFavourites);

  // I hate this but since the heirarchy logic is so far down the component chain
  // I need a way to signal to TreeSelectIncludeExclude to run the include from
  // the favourites menu. In future, the logic should be moved to Redux.
  const [externalIncludeCommand, setExternalIncludeCommand] = useState(null);

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const overflowValues = useRef([]);

  const [selectedValues, setSelectedValues] = useState(valueFromStore || []);

  const findParentKey = useCallback((nodes, value) => {
    for (const node of nodes) {
      if (node.children) {
        if (node.children.some(child => child.value === value)) {
          return node.key;
        }
        const parentKey = findParentKey(node.children, value);
        if (parentKey) {
          return parentKey;
        }
      }
    }
    return null;
  }, []);

  const findLabel = useCallback((nodes, value) => {
    for (const node of nodes) {
      if (node.value === value) {
        return node.title;
      }
      if (node.children) {
        const result = findLabel(node.children, value);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }, []);

  const dispatch = useDispatch();

  const { hasSpecificLicense } = useAuth();

  const hasIndustryFullLicenseOnly = hasSpecificLicense([
    { type: licenseType.industry, demoMode: false },
  ]);
  const hasIndustryTrialLicenseOnly = hasSpecificLicense([
    { type: licenseType.industry, demoMode: true },
  ]);
  const hasIndustryFullAndAcademicTrialLicense = hasSpecificLicense([
    { type: licenseType.industry, demoMode: false },
    { type: licenseType.academic, demoMode: true },
  ]);

  const hasAcademicFullLicenseOnly = hasSpecificLicense([
    { type: licenseType.academic, demoMode: false },
  ]);
  const hasAcademicTrialLicenseOnly = hasSpecificLicense([
    { type: licenseType.academic, demoMode: true },
  ]);
  const hasAcademicFullAndIndustryTrialLicense = hasSpecificLicense([
    { type: licenseType.academic, demoMode: false },
    { type: licenseType.industry, demoMode: true },
  ]);

  const hasAcademicAndIndustryFullLicense = hasSpecificLicense([
    { type: licenseType.academic, demoMode: false },
    { type: licenseType.industry, demoMode: false },
  ]);
  const hasAcademicAndIndustryTrialLicense = hasSpecificLicense([
    { type: licenseType.academic, demoMode: true },
    { type: licenseType.industry, demoMode: true },
  ]);
  const hasLegacyTrialLicenseOnly = hasSpecificLicense([
    { type: licenseType.legacy, demoMode: true },
  ]);

  const hasLegacyFullOnly = hasSpecificLicense([
    { type: licenseType.legacy, demoMode: false },
  ]);

  const hasLegacyFullAndIndustryTrialLicense = hasSpecificLicense([
    { type: licenseType.legacy, demoMode: false },
    { type: licenseType.industry, demoMode: true },
  ]);

  const hasLegacyFullAndIndustryFullLicense = hasSpecificLicense([
    { type: licenseType.legacy, demoMode: false },
    { type: licenseType.industry, demoMode: false },
  ]);

  let filterOptions = categoriesAcademic;

  if (
    hasAcademicAndIndustryFullLicense ||
    hasIndustryFullAndAcademicTrialLicense ||
    hasAcademicFullAndIndustryTrialLicense ||
    hasAcademicAndIndustryTrialLicense
  ) {
    filterOptions = categories;
  }

  if (hasLegacyFullOnly || hasLegacyTrialLicenseOnly) {
    filterOptions = categoriesLegacy;
  }

  if (hasAcademicFullLicenseOnly || hasAcademicTrialLicenseOnly) {
    filterOptions = categoriesAcademic;
  }

  if (hasIndustryFullLicenseOnly || hasIndustryTrialLicenseOnly) {
    filterOptions = categoriesIndustry;
  }

  if (
    hasLegacyFullAndIndustryTrialLicense ||
    hasLegacyFullAndIndustryFullLicense
  ) {
    filterOptions = categoriesLegacyIndustry;
  }

  const hasUnmatchedCategories = (valueFromStore, filterOptions) => {
    const collectValues = options => {
      return options.flatMap(option => {
        if (option.children) {
          const childrenValues = collectValues(option.children);
          return [option.value, ...childrenValues];
        }
        return option.value;
      });
    };

    const filterOptionsValues = collectValues(filterOptions);
    return valueFromStore
      .map(getStringValue)
      .some(value => !filterOptionsValues.includes(value));
  };

  const unmatched = hasUnmatchedCategories(selectedValues, filterOptions);

  /**
   * Handles toggling favourite status for a category
   * Dispatches Redux action to add/remove category from favourites with parent-child logic
   */
  const handleToggleFavourite = useCallback(
    treeItemModelKey => {
      // Toggle favourite action will do the complex tree logic
      dispatch(toggleFavouriteAction(treeItemModelKey));
    },
    [dispatch]
  );

  /**
   * Handles clicking on a starred item from the StarredSection
   * Triggers the component down the chain where the logic is to run the include logic.
   */
  const handleStarredItemClick = useCallback(
    treeItemModelKey => {
      setExternalIncludeCommand({
        category: treeItemModelKey,
        nonce: Date.now(),
      });
    },
    [setExternalIncludeCommand]
  );

  /**
   * Create flattened categories array for StarredSection path building
   * The StarredSection needs a flattened structure to build hierarchical paths for tooltips
   * This memo ensures we only recalculate when filterOptions change
   */
  const flattenedCategories = useMemo(() => {
    const flattened = [];

    // Recursive function to flatten the tree structure while preserving parent relationships
    const flatten = (items, parentPath = []) => {
      items.forEach(item => {
        // Add the item with parent information
        const itemWithParent = {
          ...item,
          parentPath: [...parentPath], // Array of parent objects from root to immediate parent
          parentValues: parentPath.map(p => p.value), // Array of parent values for easier lookup
        };
        flattened.push(itemWithParent);

        // Recursively process children if they exist
        if (item.children) {
          flatten(item.children, [...parentPath, item]);
        }
      });
    };

    flatten(filterOptions);
    return flattened;
  }, [filterOptions]);

  /**
   * Create StarredSection component instance
   * Only renders when favourites exist to avoid empty sections
   * Passes click handler and flattened categories for path building
   */
  const starredSection =
    favourites && favourites.length > 0 ? (
      <StarredSection
        onItemClick={handleStarredItemClick}
        allCategories={flattenedCategories}
      />
    ) : null;

  useEffect(() => {
    if (unmatched) {
      dispatch(setFilter(organisationCategory, []));
      setSelectedValues([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderTooltipContent = () => {
    return (
      <>
        {overflowValues.current.map(({ value: label }) => (
          <Fragment key={label}>
            <span>{label}</span>
            <br />
          </Fragment>
        ))}
      </>
    );
  };

  const updateTooltip = useCallback(() => {
    const overflowItem = document.querySelector(
      '.ant-select-selection-overflow-item-rest'
    );
    if (overflowItem) {
      const outerHTML = overflowItem.outerText;
      const isVisible = overflowItem.style.opacity !== '0';
      if (isVisible) {
        const overflowMatch = outerHTML.match(/\+ (\d+) \.\.\./);
        if (overflowMatch && parseInt(overflowMatch[1], 10) > 0) {
          if (!isOpen) {
            setTooltipVisible(true);
          }
        } else {
          setTooltipVisible(false);
        }
      } else {
        setTooltipVisible(false);
      }
    } else {
      setTooltipVisible(false);
    }
  }, []);

  const isDropdownOpened = useRef(false);
  const handleDropdownVisibleChange = open => {
    isDropdownOpened.current = open;
    if (isDropdownOpened.current) {
      setTooltipVisible(false);
    }
  };

  const handleMouseEnter = () => {
    if (!isDropdownOpened.current) {
      updateTooltip();
    }
  };

  const handleMouseLeave = () => {
    setTooltipVisible(false);
  };

  useEffect(() => {
    setSelectedValues(valueFromStore || []);
  }, [valueFromStore]);

  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    const timeout = setTimeout(() => {
      if (!isDropdownOpened.current) {
        updateTooltip();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedValues, updateTooltip]);

  const handleOrgCategoryChange = newValues => {
    if (hasLegacyFullOnly || hasLegacyTrialLicenseOnly) {
      const option1 = 'Industry (All)';
      const option2 = 'BioPharma';

      const hasOption1 = newValues.includes(option1);
      const hasOption2 = newValues.includes(option2);

      if (hasOption1 && hasOption2) {
        if (selectedValues.includes(option2)) {
          newValues = newValues.filter(value => value !== option2);
        } else {
          newValues = newValues.filter(value => value !== option1);
        }
      }
    }

    dispatch(setFilter(organisationCategory, newValues));
  };

  const abbreviationMap = {
    'Contract Development and Manufacturing Organisation (CDMO)': 'CDMO',
    'Contract Research Organisation (CRO)': 'CRO',
  };

  const tagRender = props => {
    const { onClose, value } = props;

    const label = findLabel(filterOptions, getStringValue(value));

    let displayLabel = abbreviationMap[label] || label;

    if (isExcluded(value)) {
      displayLabel = toExcluded(displayLabel);
    }
    return (
      <Token
        key={label}
        value={displayLabel}
        removeToken={onClose}
        style={{ marginTop: '2px', marginBottom: '2px' }}
        onMouseDown={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
      />
    );
  };

  return (
    <OrgCategorySection>
      <span>Category</span>
      <OrgCategoryContainer>
        <Tooltip
          title={renderTooltipContent()}
          placement={'topRight'}
          open={tooltipVisible}
          getPopupContainer={triggerNode => triggerNode.parentElement}
        >
          <Filter
            allowClear={false}
            style={{ width: '100%' }}
            size='medium'
            maxTagCount={'responsive'}
            filterType='treeSelectIncludeExclude'
            placeholder='Select organisation categories'
            filterId={organisationCategory}
            activeOnly={activeOnly}
            treeData={filterOptions}
            selectedValues={selectedValues}
            tagRender={tagRender}
            maxTagPlaceholder={val => {
              overflowValues.current = val;
              return val?.length ? <span>+ {val.length} ...</span> : null;
            }}
            multiple
            treeCheckable={true}
            showSearch={false}
            onChange={handleOrgCategoryChange}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onDropdownVisibleChange={handleDropdownVisibleChange}
            disabled={loading}
            treeDefaultExpandAll
            favourites={favourites}
            onToggleFavourite={handleToggleFavourite}
            starredSection={starredSection}
            externalIncludeCommand={externalIncludeCommand}
          />
        </Tooltip>
      </OrgCategoryContainer>
      <AiIcon>
        We are using AI and human ingenuity to better classify scientific
        companies.
      </AiIcon>
    </OrgCategorySection>
  );
};

export default OrgCategory;
